import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { LocaleLink } from "@/components/site/LocaleLink";
import { useState, useRef, useEffect } from "react";
import { Mail, MapPin, CheckCircle2, ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { contactSchema } from "@/lib/contact/schema";
import { toast } from "sonner";
import { track, trackFormFailure, trackFormSubmit } from "@/lib/analytics/track";
import { failureReasonForStatus } from "@/lib/analytics/cta-events";

import { pageSchemas } from "@/lib/seo/page-schema";
import { logFormSubmission, hubspotFailReason } from "@/lib/form-log/client";
const DESC = "Свържете се с Perceptica относно услуги за медиен мониторинг, медийно разузнаване и медийни анализи в Югоизточна Европа. На повечето запитвания отговаряме в рамките на един работен ден.";

export const Route = createFileRoute("/bg/contact-us")({
  head: () => ({
    meta: [
      { title: "Контакти - услуги за медиен мониторинг | Perceptica" },
      { name: "description", content: DESC },
      { property: "og:title", content: "Контакти - услуги за медиен мониторинг | Perceptica" },
      { property: "og:description", content: "Свържете се с Perceptica относно услуги за медиен мониторинг, медийно разузнаване и медийни анализи в Югоизточна Европа. На повечето запитвания отговаряме в рамките на един работен ден." },

      ...ogImageMeta("bg", "/contact-us", "Свържете се с Perceptica"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Контакти - услуги за медиен мониторинг | Perceptica" },
      { name: "twitter:description", content: DESC },
      { property: "og:url", content: "https://perceptica.com/bg/contact-us" },
      { property: "og:type", content: "website" },
    ],
  
    scripts: pageSchemas({ locale: "bg", path: "/contact-us", contact: true }),
  }),
  component: ContactPageBG,
});

function ContactPageBG() {
  const [submitted, setSubmitted] = useState(false);
  const [submission, setSubmission] = useState<{
    name: string;
    email: string;
    company: string;
    message: string;
    ticketId: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (submitted && successRef.current) {
      successRef.current.focus();
      successRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [submitted]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerError(null);
    setErrors({});
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      company: String(fd.get("company") ?? ""),
      message: String(fd.get("message") ?? ""),
      consent: fd.get("consent") === "on",
      website: String(fd.get("website") ?? ""),
      pagePath: typeof window !== "undefined" ? window.location.pathname : "",
      formName: "bg-contact-us",
    };
    const parsed = contactSchema.safeParse(payload);
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(flat)) if (v?.[0]) next[k] = v[0];
      setErrors(next);
      trackFormFailure({ form_name: payload.formName, reason: "validation_error", error_code: Object.keys(next).join(","), page_path: payload.pagePath, locale: "bg" });
      return;
    }
    setSubmitting(true);
    const backupBase = {
      formType: "contact",
      formName: payload.formName,
      pagePath: payload.pagePath,
      name: parsed.data.name,
      email: parsed.data.email,
      company: parsed.data.company ?? undefined,
      message: parsed.data.message,
      payload: { locale: "bg" },
    };
    try {
      const res = await fetch(
        "https://api-eu1.hsforms.com/submissions/v3/integration/submit/25788344/f5132c61-1c96-4424-a95c-c58d03756656",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fields: [
              { objectTypeId: "0-1", name: "email", value: parsed.data.email },
              { objectTypeId: "0-1", name: "fullname", value: parsed.data.name },
              { objectTypeId: "0-1", name: "message", value: parsed.data.message },
              ...(parsed.data.company
                ? [{ objectTypeId: "0-1", name: "company", value: parsed.data.company }]
                : []),
            ],
            context: {
              pageUri: typeof window !== "undefined" ? window.location.href : "",
              pageName: typeof document !== "undefined" ? document.title : "",
            },
            legalConsentOptions: {
              consent: {
                consentToProcess: true,
                text: "Съгласен/на съм екипът на Perceptica да се свърже с мен.",
              },
            },
          }),
        },
      );
      const data = (await res.json().catch(() => ({}))) as { inlineMessage?: string; message?: string };
      if (!res.ok) {
        void logFormSubmission({ ...backupBase, hubspotOk: false, hubspotReason: hubspotFailReason(res.status) });
        setServerError(data.message ?? "Възникна грешка. Моля, опитайте отново.");
        trackFormFailure({ form_name: payload.formName, reason: failureReasonForStatus(res.status), error_code: res.status, page_path: payload.pagePath, locale: "bg" });
        return;
      }
      setSubmitted(true);
      void logFormSubmission({ ...backupBase, hubspotOk: true });
      trackFormSubmit("submit_contact_us", { form_name: payload.formName, page_path: payload.pagePath, locale: "bg" });
      setSubmission({
        name: parsed.data.name,
        email: parsed.data.email,
        company: parsed.data.company ?? "",
        message: parsed.data.message,
        ticketId: `PCP-${Date.now().toString(36).toUpperCase().slice(-6)}`,
      });
    } catch {
      void logFormSubmission({ ...backupBase, hubspotOk: false, hubspotReason: hubspotFailReason(null) });
      setServerError("Мрежова грешка. Моля, опитайте отново.");
      trackFormFailure({ form_name: payload.formName, reason: "network_error", page_path: payload.pagePath, locale: "bg" });
      toast.error("Мрежова грешка. Моля, опитайте отново.");
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "w-full rounded-md border border-border/70 bg-background px-3 py-2.5 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30";

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-[1100px] gap-16 px-6 lg:grid-cols-[1fr_1.1fr] lg:px-10">
          <div>
            <span className="inline-flex items-center rounded-full border border-border/70 bg-secondary/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Контакт
            </span>
            <h1 className="font-display mt-5 text-[clamp(2rem,3.6vw,3rem)] font-medium leading-[1.05] text-foreground">
              Говорете с екипа на Perceptica.
            </h1>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-foreground/75">
              Истински човек - без чатбот, без фуния. На повечето запитвания отговаряме в рамките на един работен ден.
            </p>

            <dl className="mt-9 space-y-4 text-[14px] text-foreground/85">
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 flex-none text-accent" />
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Имейл</dt>
                  <dd><a href="mailto:sales@perceptica.com" className="hover:text-accent">sales@perceptica.com</a></dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 flex-none text-accent" />
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Централа</dt>
                  <dd>бул. Христо Ботев 117, 1303 София</dd>
                </div>
              </div>
            </dl>

            <LocaleLink
              to="/request-a-demo"
              className="group mt-9 flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-secondary/40 p-5 transition-colors hover:border-accent/50"
            >
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                  Търсите демонстрация?
                </p>
                <p className="mt-1 text-[14px] font-medium text-foreground">
                  Заявете демо на продукта вместо това
                </p>
              </div>
              <ArrowRight className="h-4 w-4 flex-none text-accent transition-transform group-hover:translate-x-1" />
            </LocaleLink>
          </div>

          <div>
            {submitted && submission ? (
              <div
                ref={successRef}
                tabIndex={-1}
                role="status"
                aria-live="polite"
                className="overflow-hidden rounded-2xl border border-border/60 bg-background p-7 shadow-[var(--shadow-soft)] focus:outline-none"
              >
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-accent/15 text-accent ring-1 ring-accent/30">
                    <CheckCircle2 className="h-6 w-6" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-accent">Съобщението е изпратено</p>
                    <h2 className="font-display mt-1 text-[1.55rem] leading-tight text-foreground">
                      Благодарим, {submission.name.split(" ")[0]} - получихме го.
                    </h2>
                    <p className="mt-2 text-[14px] leading-relaxed text-foreground/75">
                      На повечето запитвания отговаряме в рамките на един работен ден. Ще изпратим отговора си на <span className="font-medium text-foreground">{submission.email}</span>.
                    </p>
                    <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      Референция {submission.ticketId}
                    </p>
                  </div>
                </div>
                <div className="mt-7 flex flex-wrap gap-3 border-t border-border/60 pt-6">
                  <Button asChild size="lg">
                    <LocaleLink to="/request-a-demo">Заявете демо</LocaleLink>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => {
                      setSubmitted(false);
                      setSubmission(null);
                    }}
                  >
                    Изпратете ново съобщение
                  </Button>
                </div>
              </div>
            ) : (
              <form name="bg-contact-us" id="bg-contact-us" data-hs-do-not-collect="true" onSubmit={handleSubmit} noValidate className="space-y-5 rounded-2xl border border-border/60 bg-background p-7 shadow-[var(--shadow-soft)]">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Име" name="name" error={errors.name} inputCls={inputCls} required />
                  <Field label="Имейл" name="email" type="email" error={errors.email} inputCls={inputCls} required />
                </div>
                <Field label="Компания (по избор)" name="company" error={errors.company} inputCls={inputCls} />
                <div>
                  <label htmlFor="message" className="mb-1.5 block text-[12.5px] font-semibold text-foreground/90">
                    Съобщение <span className="text-accent">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    className={inputCls}
                    placeholder="За какво бихте искали да говорим?"
                  />
                  {errors.message && <p className="mt-1 text-[12px] text-destructive">{errors.message}</p>}
                </div>

                {/* Honeypot */}
                <div className="hidden" aria-hidden>
                  <label>
                    Website
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                  </label>
                </div>

                <label className="flex items-start gap-3 text-[12.5px] text-foreground/75">
                  <input type="checkbox" name="consent" required className="mt-0.5 h-4 w-4 rounded border-border accent-accent" />
                  <span>
                    Съгласен/на съм Perceptica да обработва данните ми съгласно{" "}
                    <LocaleLink to="/privacy-policy" className="font-semibold text-accent hover:underline">Политиката за поверителност</LocaleLink>.
                  </span>
                </label>
                {errors.consent && <p className="text-[12px] text-destructive">{errors.consent}</p>}

                {serverError && <p className="text-[13px] text-destructive">{serverError}</p>}

                <Button type="submit" size="lg" disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? "Изпращане…" : "Изпратете съобщение"}
                </Button>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  На повечето запитвания отговаряме в рамките на един работен ден.
                </p>
              </form>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  error,
  inputCls,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  error?: string;
  inputCls: string;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-[12.5px] font-semibold text-foreground/90">
        {label} {required && <span className="text-accent">*</span>}
      </label>
      <input id={name} name={name} type={type} required={required} className={inputCls} />
      {error && <p className="mt-1 text-[12px] text-destructive">{error}</p>}
    </div>
  );
}
